import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, FileText, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { FeedbackTemplateEditor } from '../components/FeedbackTemplateEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exposed so other pages can refresh the templates list
export const InductionTemplatesManager = ({ onChange }) => {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { new:true } or full template
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/induction-feedback-templates`, { headers: getAuthHeader() });
    if (r.ok) { const d = await r.json(); setRows(d); onChange && onChange(d); }
    setLoading(false);
  }, [getAuthHeader, onChange]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ new: true });
  const openEdit = async (t) => {
    // fetch the full template (list rows may omit heavy fields)
    const r = await fetch(`${API}/induction-feedback-templates/${t.template_id}`, { headers: getAuthHeader() });
    setEditing(r.ok ? await r.json() : t);
  };

  const save = async (payload) => {
    setSaving(true);
    const isNew = editing?.new;
    const url = isNew ? `${API}/induction-feedback-templates` : `${API}/induction-feedback-templates/${editing.template_id}`;
    const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    setSaving(false);
    if (r.ok) { toast.success(isNew ? 'Template created' : 'Template updated'); setEditing(null); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Save failed'); }
  };

  const duplicate = async (t) => {
    const r = await fetch(`${API}/induction-feedback-templates/${t.template_id}/duplicate`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { toast.success('Template duplicated'); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Duplicate failed'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    const r = await fetch(`${API}/induction-feedback-templates/${t.template_id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Template deleted'); load(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="feedback-templates-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Reusable feedback forms. Assign one to each induction session.</p>
        <Button size="sm" onClick={openNew} data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" /> New Template</Button>
      </div>

      {loading ? <p className="text-muted-foreground text-sm">Loading...</p>
        : rows.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-muted-foreground" data-testid="templates-empty">
            <FileText className="h-9 w-9 mx-auto mb-2 opacity-40" /><p>No feedback templates yet.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rows.map(t => (
              <Card key={t.template_id} data-testid={`template-card-${t.template_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{t.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.section_count ? `${t.section_count} section(s) · ` : ''}{t.question_count} question(s) · used by {t.in_use} induction(s)
                      </p>
                      {t.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-template-${t.template_id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => duplicate(t)} data-testid={`duplicate-template-${t.template_id}`}><Copy className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(t)} data-testid={`delete-template-${t.template_id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader><DialogTitle>{editing?.new ? 'New Feedback Template' : 'Edit Feedback Template'}</DialogTitle></DialogHeader>
          {editing && (
            <FeedbackTemplateEditor
              initial={editing?.new ? null : editing}
              saving={saving}
              onSubmit={save}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
